import { useCallback, useEffect, useState } from "react";

export type DetailViewMode = "panel" | "modal";

const STORAGE_KEY = "tt-orders-detail-view-mode";
const DEFAULT_MODE: DetailViewMode = "panel";

function readStoredMode(): DetailViewMode {
  if (typeof window === "undefined") return DEFAULT_MODE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "panel" || raw === "modal") return raw;
  } catch {
    // ignore (e.g. private browsing)
  }
  return DEFAULT_MODE;
}

/**
 * Persisted toggle for the TT Orders detail view (side panel vs modal).
 * Backed by localStorage under "tt-orders-detail-view-mode".
 */
export function useDetailViewMode(): {
  mode: DetailViewMode;
  setMode: (mode: DetailViewMode) => void;
  toggleMode: () => void;
} {
  const [mode, setModeState] = useState<DetailViewMode>(readStoredMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }, [mode]);

  const setMode = useCallback((next: DetailViewMode) => {
    setModeState(next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev: DetailViewMode) => (prev === "panel" ? "modal" : "panel"));
  }, []);

  return { mode, setMode, toggleMode };
}
